<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-12">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <h4 class="font-weight-bold mb-2">계정 채널 상태</h4>
          <div class="d-flex align-items-center mb-2">
            <button class="btn btn-primary btn-sm mr-2" @click="fetchRows"><i class="fas fa-sync-alt mr-1"></i> 새로고침</button>
            <span class="text-muted small">마지막 업데이트: {{ lastUpdated }}</span>
          </div>
        </div>
        <p class="text-muted small mb-3">고객 PC 의 확장·CLI 가 계정마다 채널 상태가 바뀔 때(그리고 하루 한 번) 보낸 값입니다. 아이디·비밀번호·쿠키는 받지 않습니다.</p>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body py-2">
            <div class="d-flex flex-wrap align-items-center">
              <input type="text" class="form-control form-control-sm mr-2 mb-2 cs-filter" v-model="filterCompanyId" placeholder="회사 ID" @keyup.enter="fetchRows" />
              <input type="text" class="form-control form-control-sm mr-2 mb-2 cs-filter" v-model="filterSupplierCode" placeholder="판매자코드" @keyup.enter="fetchRows" />
              <select class="form-control form-control-sm mr-2 mb-2 cs-filter-sm" v-model="filterSource">
                <option value="">보낸 쪽 전체</option>
                <option value="ext">확장</option>
                <option value="cli">CLI</option>
              </select>
              <button class="btn btn-sm btn-success mb-2" @click="fetchRows"><i class="fas fa-search"></i> 조회</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-header"><h5 class="mb-0">계정 {{ rows.length }}줄</h5></div>
          <div class="card-body cs-scroll">
            <div v-if="loading" class="text-center py-5"><b-spinner variant="primary"></b-spinner></div>
            <div v-else-if="error" class="text-center py-4 text-danger">{{ error }}</div>
            <div v-else-if="rows.length === 0" class="text-center py-5 text-muted">조회된 계정이 없습니다.</div>
            <table v-else class="table table-sm cs-table">
              <thead>
                <tr><th>회사</th><th>판매자코드</th><th>보낸 쪽</th><th v-for="c in channels" :key="c.key">{{ c.label }}</th><th>1688</th><th>판</th><th>마지막 보고</th><th>바뀐 시각</th></tr>
              </thead>
              <tbody>
                <template v-for="r in rows">
                  <tr :key="r._id" class="cs-row" @click="toggle(r)">
                    <td data-label="회사">{{ r.companyid }}</td>
                    <td data-label="판매자코드"><code>{{ r.suppliercode }}</code></td>
                    <td data-label="보낸 쪽">{{ r.source === 'ext' ? '확장' : 'CLI' }}</td>
                    <td v-for="c in channels" :key="c.key" :data-label="c.label">
                      <span class="badge" :class="channelBadge(r.state, c.key).cls">{{ channelBadge(r.state, c.key).text }}</span>
                      <span v-if="loggedOutFor(r.state, c.key)" class="small text-danger ml-1">{{ loggedOutFor(r.state, c.key) }}</span>
                    </td>
                    <td data-label="1688">{{ ali(r.state) }}</td>
                    <td data-label="판" class="small">{{ version(r.state) }}</td>
                    <td data-label="마지막 보고" class="small">{{ formatTime(r.lastSeenAt) }}</td>
                    <td data-label="바뀐 시각" class="small">{{ formatTime(r.changedAt) }}</td>
                  </tr>
                  <tr v-if="openId === r._id" :key="r._id + '-h'" class="cs-history">
                    <td colspan="99">
                      <div v-if="historyLoading" class="small text-muted">기록 불러오는 중…</div>
                      <div v-else-if="!history.length" class="small text-muted">바뀐 기록이 없습니다.</div>
                      <ul v-else class="list-unstyled small mb-0">
                        <li v-for="(h, i) in history" :key="i">{{ formatTime(h.at) }} — {{ summary(h.state) }}</li>
                      </ul>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const STATE_TEXT = { ok: '정상', 'logged-out': '풀림', fail: '실패', mismatch: '다른 계정', noPermission: '권한 없음', unknown: '모름' };
// 1688 상태 낱말(보내는 쪽 TOKEN) — 모르는 낱말은 그대로 보인다
const ALI_TEXT = { 'logged-in': '로그인됨', 'needs-login': '로그인 필요', 'logged-out': '풀림', failed: '실패', unknown: '모름' };

export default {
  name: 'ChannelState',
  data() {
    return {
      loading: false, error: '', rows: [], lastUpdated: '-',
      filterCompanyId: '', filterSupplierCode: '', filterSource: '',
      openId: null, history: [], historyLoading: false,
      channels: [{ key: 'wing', label: '윙' }, { key: 'supplier', label: '서플라이어' }, { key: 'advertising', label: '광고센터' }],
    };
  },
  mounted() { this.fetchRows(); },
  methods: {
    params(extra) {
      const p = {};
      if (this.filterCompanyId.trim()) p.companyid = this.filterCompanyId.trim();
      if (this.filterSupplierCode.trim()) p.suppliercode = this.filterSupplierCode.trim();
      if (this.filterSource) p.source = this.filterSource;
      return { ...p, ...(extra || {}) };
    },
    async fetchRows() {
      this.loading = true;
      this.error = '';
      this.openId = null;
      try {
        const res = await axios.get('/api/admin/channel-state', { params: this.params() });
        if (res.data && res.data.success) this.rows = res.data.rows || [];
        else this.error = (res.data && res.data.message) || '조회하지 못했습니다.';
        this.lastUpdated = new Date().toLocaleTimeString();
      } catch (e) {
        console.error('채널 상태 조회 실패:', e);
        this.error = '조회하지 못했습니다.';
      } finally {
        this.loading = false;
      }
    },
    async toggle(r) {
      if (this.openId === r._id) { this.openId = null; return; }
      this.openId = r._id;
      this.history = [];
      this.historyLoading = true;
      try {
        const res = await axios.get('/api/admin/channel-state', { params: { companyid: r.companyid, suppliercode: r.suppliercode, source: r.source } });
        const row = res.data && res.data.success && (res.data.rows || [])[0];
        this.history = row && Array.isArray(row.history) ? row.history.slice().reverse() : [];
      } catch (e) {
        console.error('채널 상태 기록 조회 실패:', e);
      } finally {
        this.historyLoading = false;
      }
    },
    // 채널 칸 — 쓰는지(channels) + 마지막 확인 상태(check 또는 keepalive domains)
    channelBadge(st, ch) {
      const s = st || {};
      const on = (s.channels || {})[ch];
      const state = ((s.check || {})[ch] || (s.domains || {})[ch] || {}).state;
      if (on === false) return { text: '권한 없음', cls: 'badge-secondary' };
      if (!state) return { text: on === true ? '켜짐' : '-', cls: on === true ? 'badge-light' : 'badge-light text-muted' };
      const cls = state === 'ok' ? 'badge-success' : state === 'unknown' ? 'badge-light' : 'badge-danger';
      return { text: STATE_TEXT[state] || state, cls };
    },
    loggedOutFor(st, ch) {
      const d = ((st || {}).domains || {})[ch];
      if (!d || d.state !== 'logged-out' || !d.since) return '';
      const h = Math.floor((Date.now() - d.since) / 3600000);
      return h >= 24 ? `${Math.floor(h / 24)}일째` : `${h}시간째`;
    },
    ali(st) {
      const a = (st || {}).ali1688;
      if (!a) return '-';
      const text = ALI_TEXT[a.state] || a.state;
      return a.needsRelogin && a.state !== 'needs-login' ? `${text} · 재로그인 필요` : text;
    },
    version(st) {
      const c = (st || {}).client || {};
      return [c.extVersion && `확장 ${c.extVersion}`, c.cliVersion && `CLI ${c.cliVersion}`].filter(Boolean).join(' · ') || '-';
    },
    summary(st) {
      return this.channels.map(c => `${c.label} ${this.channelBadge(st, c.key).text}`).join(' · ');
    },
    formatTime(ts) {
      if (!ts) return '-';
      const d = new Date(ts);
      return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    },
  },
};
</script>

<style scoped>
.card { box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
.cs-filter { width: 160px; }
.cs-filter-sm { width: 130px; }
.cs-row { cursor: pointer; }
/* 넓은 화면에서 칸이 카드보다 길면 표 안에서 옆으로 민다(오른쪽 칸 잘림 0) */
.cs-scroll { overflow-x: auto; }
.cs-table td, .cs-table th { vertical-align: middle; white-space: nowrap; }
.cs-history td { background: #f8f9fa; white-space: normal; }
/* 좁은 화면 — 줄을 카드로(머리 숨김 · 칸 이름은 data-label) */
@media (max-width: 576px) {
  .cs-filter, .cs-filter-sm { width: 100%; }
  .cs-table thead { display: none; }
  .cs-table tr { display: block; border: 1px solid #dee2e6; border-radius: 6px; margin-bottom: 10px; padding: 6px 8px; }
  .cs-table td { display: flex; justify-content: space-between; align-items: center; border: 0; padding: 3px 0; white-space: normal; }
  .cs-table td::before { content: attr(data-label); font-weight: 600; color: #6c757d; margin-right: 12px; }
  .cs-history td::before { content: none; }
}
</style>
